export interface TextChange {
  start: number;
  end: number;
  original: string;
  replacement: string;
}

/** `insertionPoint` is a zero-width spot where a pure insert lands; it has no text. */
export interface OriginalSegment {
  text: string;
  kind: 'plain' | 'struck' | 'insertionPoint';
}

/** `removalPoint` is a zero-width spot where a pure delete was; it has no text. */
export interface CorrectedSegment {
  text: string;
  kind: 'plain' | 'added' | 'removalPoint';
}

export interface HighlightRange {
  start: number;
  end: number;
}

export interface AppliedChanges {
  originalSegments: OriginalSegment[];
  correctedText: string;
  /** Ranges of inserted text in `correctedText`. A pure delete has no range. */
  highlights: HighlightRange[];
  correctedSegments: CorrectedSegment[];
  /** How many changes were applied; skipped changes are not counted. */
  appliedCount: number;
}

export const isApplicable = (text: string, change: TextChange): boolean =>
  Number.isInteger(change.start) &&
  Number.isInteger(change.end) &&
  change.start >= 0 &&
  change.start <= change.end &&
  change.end <= text.length &&
  text.slice(change.start, change.end) === change.original &&
  (change.original !== '' || change.replacement !== '');

/**
 * Applies changes to a paragraph's text. Offsets are JavaScript string indexes (UTF-16 code units).
 * A change whose range is outside the text, or whose `original` doesn't match the text at that
 * range, is skipped, as is a no-op or one that overlaps an earlier change. Never throws.
 */
export function applyChangesToText(text: string, changes: readonly TextChange[]): AppliedChanges {
  const applied: TextChange[] = [];
  for (const change of [...changes].sort((a, b) => a.start - b.start || a.end - b.end)) {
    const previous = applied[applied.length - 1];
    if (!isApplicable(text, change) || (previous && change.start < previous.end)) continue;
    applied.push(change);
  }

  const originalSegments: OriginalSegment[] = [];
  let cursor = 0;
  for (const { start, end } of applied) {
    if (start > cursor) originalSegments.push({ text: text.slice(cursor, start), kind: 'plain' });
    if (end > start) originalSegments.push({ text: text.slice(start, end), kind: 'struck' });
    else originalSegments.push({ text: '', kind: 'insertionPoint' });
    cursor = end;
  }
  if (cursor < text.length) originalSegments.push({ text: text.slice(cursor), kind: 'plain' });

  let correctedText = text;
  for (let index = applied.length - 1; index >= 0; index -= 1) {
    const { start, end, replacement } = applied[index];
    correctedText = correctedText.slice(0, start) + replacement + correctedText.slice(end);
  }

  const highlights: HighlightRange[] = [];
  const correctedSegments: CorrectedSegment[] = [];
  let correctedCursor = 0;
  let delta = 0;
  for (const { start, end, replacement } of applied) {
    const correctedStart = start + delta;
    if (correctedStart > correctedCursor) {
      correctedSegments.push({
        text: correctedText.slice(correctedCursor, correctedStart),
        kind: 'plain',
      });
    }
    if (replacement === '') {
      correctedSegments.push({ text: '', kind: 'removalPoint' });
    } else {
      const correctedEnd = correctedStart + replacement.length;
      highlights.push({ start: correctedStart, end: correctedEnd });
      correctedSegments.push({
        text: correctedText.slice(correctedStart, correctedEnd),
        kind: 'added',
      });
    }
    correctedCursor = correctedStart + replacement.length;
    delta += replacement.length - (end - start);
  }
  if (correctedCursor < correctedText.length) {
    correctedSegments.push({ text: correctedText.slice(correctedCursor), kind: 'plain' });
  }

  return {
    originalSegments,
    correctedText,
    highlights,
    correctedSegments,
    appliedCount: applied.length,
  };
}
